import { useState } from 'react';

import { Stack, Typography } from '@mui/material';
import { GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import useSWR from 'swr';

import { Card, DataGrid, DataGridToolbar, useColDefs } from '~components';
import { generalInformation, pagePathSegments, queryParameters } from '~constants';
import { DataGridToolBarFilterType } from '~enums';
import ResourceGroup from '~interfaces/resourceGroup';
import { cancellationMiddleware } from '~lib/swr';
import { getService } from '~services/serviceFactory';

import Dimens from '../../../themes/dimens';
import ResourceGroupService from '../services/resourceGroupService';

const service = getService(ResourceGroupService);
const persistenceKey = 'users';

interface ResourceGroupsDataGridProps {
	showTitle?: boolean;
	overwrittenClientId?: string;
}

const ResourceGroupsDataGrid = ({
	showTitle = false,
	overwrittenClientId,
}: ResourceGroupsDataGridProps) => {
	// Hooks
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();

	// Data loading
	const { data, error, isLoading, isValidating } = useSWR(
		service.getResourceGroupsCacheKey(searchParams, overwrittenClientId),
		(
			[_path, _searchParams, overwrittenClientId]: [
				'resource-groups',
				URLSearchParams,
				string | undefined,
			],
			options: { signal?: AbortSignal }
		) => {
			const page = searchParams.get(queryParameters.Page) ?? '1';
			const pageSize = searchParams.get(queryParameters.PageSize) ?? '10';
			const searchQuery = searchParams.get(queryParameters.SearchQuery) ?? undefined;

			return service.getResourceGroups({
				page: page,
				pageSize: pageSize,
				searchQuery: searchQuery,
				overwrittenClientId: overwrittenClientId,
				signal: options.signal,
			});
		},
		{
			onSuccess: (data) => setTotalCount(data?.total || 0),
			keepPreviousData: true,
			use: [cancellationMiddleware],
		}
	);
	const [totalCount, setTotalCount] = useState<number>(data?.total || 0);

	// Filter updating
	const handlePaginationChange = (model: GridPaginationModel) => {
		setSearchParams((prev) => {
			const next = prev;
			next.set(queryParameters.Page, (model.page + 1).toString());
			next.set(queryParameters.PageSize, model.pageSize.toString());
			return next;
		});
	};

	// Columns
	const colDefs = useColDefs<ResourceGroup>();
	const columns: GridColDef<ResourceGroup>[] = [
		colDefs.id(
			pagePathSegments.ResourceGroups,
			(value: ResourceGroup) => [
				[
					queryParameters.OverwrittenClientId,
					overwrittenClientId ??
						value.organisationId ??
						generalInformation.manufacturingOrganisationId,
				],
			],
			false
		),
		colDefs.name(),
	];

	const filters = [
		{
			type: DataGridToolBarFilterType.Search,
		},
	];

	return (
		<Card>
			<Stack spacing={Dimens.spacingXl}>
				{showTitle && (
					<Typography variant='labelLg'>
						{t('resourceGroup.labels.plural', { ns: 'entities' })}
					</Typography>
				)}
				<DataGridToolbar filters={filters} />
				<DataGrid
					getRowId={(row) => row.id}
					persistenceKey={persistenceKey}
					editMode='row'
					autoHeight
					disableRowSelectionOnClick
					error={error != null}
					loading={isLoading || isValidating}
					columns={columns}
					rows={data?.results ?? []}
					rowCount={totalCount}
					onPaginationModelChange={handlePaginationChange}
					paginationModel={{
						page: Number(searchParams.get(queryParameters.Page)),
						pageSize: Number(searchParams.get(queryParameters.PageSize)),
					}}
				/>
			</Stack>
		</Card>
	);
};

export default ResourceGroupsDataGrid;
